import { useEffect, useRef, type ReactNode } from 'react';
import { LogOut, X } from 'lucide-react';

export default function MobileNavigation({ open, onClose, items, selected, onSelect, userName, onLogout }: {
  open: boolean; onClose: () => void;
  items: Array<{ key: string; label: string; icon: ReactNode }>;
  selected: string; onSelect: (key: string) => void; userName: string; onLogout: () => void;
}) {
  const panel = useRef<HTMLElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const closeCallback = useRef(onClose);
  closeCallback.current = onClose;
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    close.current?.focus();
    const desktop = window.matchMedia('(min-width: 1280px)');
    const resize = () => { if (desktop.matches) closeCallback.current(); };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeCallback.current(); }
      if (event.key !== 'Tab') return;
      const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href]') ?? []);
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls.at(-1)?.focus(); }
      if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0]?.focus(); }
    };
    desktop.addEventListener('change', resize);
    window.addEventListener('keydown', keyDown);
    return () => {
      document.body.style.overflow = overflow;
      desktop.removeEventListener('change', resize);
      window.removeEventListener('keydown', keyDown);
      if (previous?.isConnected && previous.getClientRects().length) previous.focus();
    };
  }, [open]);
  if (!open) return null;
  return <aside ref={panel} id="main-mobile-navigation" role="dialog" aria-modal="true" aria-label="Ana menü" className="fixed inset-0 z-[100] flex h-[100dvh] flex-col bg-[#FCFCFC] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] xl:hidden">
    <div className="flex shrink-0 items-center justify-between px-5 pb-1 pt-3">
      <span className="font-black text-slate-800">GES Master</span>
      <button ref={close} type="button" onClick={onClose} className="inline-flex min-h-11 items-center gap-2 rounded-full border border-slate-200 px-4 text-sm font-semibold text-slate-600"><span>Kapat</span><X size={18} /></button>
    </div>
    <nav aria-label="Uygulama bölümleri" className="min-h-0 flex-1 space-y-2 overflow-y-auto px-5 py-3">
      {items.map(item => <button key={item.key} type="button" aria-current={selected === item.key ? 'page' : undefined} onClick={() => { onSelect(item.key); onClose(); }} className={`flex min-h-14 w-full items-center gap-4 rounded-2xl px-4 py-3 text-left text-base font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${selected === item.key ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-500 hover:bg-slate-100'}`}><span className="shrink-0">{item.icon}</span>{item.label}</button>)}
    </nav>
    <div className="shrink-0 border-t border-slate-100 px-5 py-3">
      <div className="truncate px-4 py-2 text-xs font-semibold text-slate-500">{userName}</div>
      <button type="button" onClick={onLogout} className="flex min-h-11 w-full items-center gap-3 rounded-xl px-4 text-sm font-bold text-rose-600 hover:bg-rose-50"><LogOut size={20} />Oturumu Kapat</button>
    </div>
  </aside>;
}
